import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LogoShield } from '@/components/LogoShield';
import { Helmet } from '@/components/Helmet';
import { Loader2, Shield, ArrowLeft } from 'lucide-react';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { AuthenticatedHeader } from '@/components/portal/AuthenticatedHeader';

const maskIp = (ip?: string | null) => {
  if (!ip) return ip;
  if (ip.includes('.')) {
    const parts = ip.split('.');
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.xxx`;
  }
  if (ip.includes(':')) {
    const parts = ip.split(':');
    return parts.slice(0, 3).join(':') + ':xxxx';
  }
  return ip;
};

export default function SecurityAudit() {
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<{
    userId: string;
    limit: number;
    offset: number;
    actionCred: string;
    actionRate: string;
    actionLogin: string;
    owaspId: string;
    controlKey: string;
    controlStatus: string;
  }>({
    userId: '',
    limit: 50,
    offset: 0,
    actionCred: '',
    actionRate: '',
    actionLogin: '',
    owaspId: '',
    controlKey: '',
    controlStatus: '',
  });

  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  const auditQuery = useQuery({
    queryKey: ['security-audit', filters],
    queryFn: async () => {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('admin-security-audit', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: {
          userId: filters.userId || undefined,
          actionCred: filters.actionCred || undefined,
          actionRate: filters.actionRate || undefined,
          actionLogin: filters.actionLogin || undefined,
          owaspId: filters.owaspId || undefined,
          controlKey: filters.controlKey || undefined,
          controlStatus: filters.controlStatus || undefined,
          limit: filters.limit,
          offset: filters.offset,
        },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Unable to load audit logs');
      return {
        credentialLogs: data?.credentialLogs || [],
        rateLimitAttempts: data?.rateLimitAttempts || [],
        loginLocations: data?.loginLocations || [],
        domainLogs: data?.domainLogs || [],
        totals: data?.totals || { credentialLogs: 0, rateLimitAttempts: 0, loginLocations: 0, domainLogs: 0 },
      };
    },
    enabled: !!user && isAdmin === true,
  });

  const hasNext = useMemo(() => {
    return (
      (auditQuery.data?.totals?.rateLimitAttempts || 0) > filters.offset + filters.limit ||
      (auditQuery.data?.totals?.loginLocations || 0) > filters.offset + filters.limit ||
      (auditQuery.data?.totals?.credentialLogs || 0) > filters.offset + filters.limit ||
      (auditQuery.data?.totals?.domainLogs || 0) > filters.offset + filters.limit
    );
  }, [auditQuery.data, filters.limit, filters.offset]);

  const credentialActions = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.credentialLogs?.forEach((r: any) => r.action && set.add(r.action));
    return Array.from(set);
  }, [auditQuery.data?.credentialLogs]);

  const rateActions = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.rateLimitAttempts?.forEach((r: any) => r.action && set.add(r.action));
    return Array.from(set);
  }, [auditQuery.data?.rateLimitAttempts]);

  const loginActions = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.loginLocations?.forEach((r: any) => r.action && set.add(r.action));
    return Array.from(set);
  }, [auditQuery.data?.loginLocations]);

  const owaspIds = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.domainLogs?.forEach((r: any) => r.owasp_id && set.add(r.owasp_id));
    return Array.from(set);
  }, [auditQuery.data?.domainLogs]);

  const controlKeys = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.domainLogs?.forEach((r: any) => r.control_key && set.add(r.control_key));
    return Array.from(set);
  }, [auditQuery.data?.domainLogs]);

  const controlStatuses = useMemo(() => {
    const set = new Set<string>();
    auditQuery.data?.domainLogs?.forEach((r: any) => r.status && set.add(r.status));
    return Array.from(set);
  }, [auditQuery.data?.domainLogs]);

  useEffect(() => {
    if (!checking && !loading && isAdmin === false) {
      navigate('/portal');
    }
  }, [checking, loading, isAdmin, navigate]);

  if (checking || loading || isAdminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    navigate('/portal');
    return null;
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Security Audit | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta
          name="description"
          content="Admin security audit logs for rate limits and login locations."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <AuthenticatedHeader
        userEmail={user?.email || ''}
        context="admin"
        roleLabel="Admin"
        showLiveScan={true}
        showPromotions={true}
        leftSlot={(
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Admin Console</span>
            </div>
          </div>
        )}
        centerSlot={(
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Admin console home
          </Button>
        )}
      />

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-5xl space-y-6">
        <Card className="cyber-card">
          <CardHeader className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Audit Logs</CardTitle>
            </div>
            <CardDescription>Filter, page, and review recent security events across all users.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Filter by user ID"
                  value={filters.userId}
                  onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value, offset: 0 }))}
                  className="w-full sm:w-56"
                />
                <select
                  value={filters.limit}
                  onChange={(e) => setFilters((prev) => ({ ...prev, limit: Number(e.target.value), offset: 0 }))}
                  className="px-3 py-2 rounded-md border border-border/50 bg-background/70 text-sm"
                >
                  {[25, 50, 100, 150, 200].map((n) => (
                    <option key={n} value={n}>{n} rows</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      offset: Math.max(prev.offset - prev.limit, 0),
                    }))
                  }
                  disabled={filters.offset <= 0 || auditQuery.isFetching}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      offset: prev.offset + prev.limit,
                    }))
                  }
                  disabled={!hasNext || auditQuery.isFetching}
                >
                  Next
                </Button>
                <Button size="sm" variant="outline" onClick={() => auditQuery.refetch()} disabled={auditQuery.isFetching}>
                  {auditQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Refresh
                </Button>
              </div>
            </div>

            {auditQuery.isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading audit logs...
              </div>
            ) : auditQuery.error ? (
              <div className="text-sm text-destructive">Unable to load audit logs.</div>
            ) : (
              <div className="space-y-4">
                <Card className="border-border/60">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm">Credential & Actions</CardTitle>
                      {credentialActions.length > 0 && (
                        <select
                          className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                          value={filters.actionCred}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, actionCred: e.target.value, offset: 0 }))
                          }
                        >
                          <option value="">All actions</option>
                          {credentialActions.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-auto pr-2">
                    {auditQuery.data?.credentialLogs?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No records.</p>
                    ) : auditQuery.data?.credentialLogs?.map((log: any, idx: number) => (
                      <div key={idx} className="p-2 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="font-mono break-all">{log.email || log.user_id || 'anon'}</span>
                          <span>{new Date(log.accessed_at).toLocaleString()}</span>
                        </div>
                        <div>Action: {log.action}</div>
                        <div>Domain: {log.domain_id || 'n/a'}</div>
                        <div>IP: {maskIp(log.ip_address) || 'n/a'}</div>
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="text-muted-foreground break-all">
                            Meta: {JSON.stringify(log.metadata)}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-border/60">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm">Rate Limit Attempts</CardTitle>
                      {rateActions.length > 0 && (
                        <select
                          className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                          value={filters.actionRate}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, actionRate: e.target.value, offset: 0 }))
                          }
                        >
                          <option value="">All actions</option>
                          {rateActions.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-auto pr-2">
                    {auditQuery.data?.rateLimitAttempts?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No records.</p>
                    ) : auditQuery.data?.rateLimitAttempts?.map((attempt: any, idx: number) => (
                      <div key={idx} className="p-2 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="font-mono break-all">{attempt.email || attempt.user_id || 'anon'}</span>
                          <span>{new Date(attempt.occurred_at).toLocaleString()}</span>
                        </div>
                        <div>Action: {attempt.action}</div>
                        <div>IP: {maskIp(attempt.ip_address) || 'n/a'}</div>
                        {attempt.location && (
                          <div className="text-muted-foreground">
                            {attempt.location.city || attempt.location.country
                              ? [attempt.location.city, attempt.location.country].filter(Boolean).join(', ')
                              : 'Location unavailable'}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-border/60">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm">Login Locations</CardTitle>
                      {loginActions.length > 0 && (
                        <select
                          className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                          value={filters.actionLogin}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, actionLogin: e.target.value, offset: 0 }))
                          }
                        >
                          <option value="">All actions</option>
                          {loginActions.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-auto pr-2">
                    {auditQuery.data?.loginLocations?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No records.</p>
                    ) : auditQuery.data?.loginLocations?.map((loc: any, idx: number) => (
                      <div key={idx} className="p-2 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="font-mono break-all">{loc.email || loc.user_id || 'n/a'}</span>
                          <span>{new Date(loc.last_seen_at).toLocaleString()}</span>
                        </div>
                        <div>IP: {maskIp(loc.ip_address) || 'n/a'}</div>
                        <div>{[loc.city, loc.country].filter(Boolean).join(', ') || 'Unknown location'}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-border/60">
                  <CardHeader>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <CardTitle className="text-sm">Security Activity (Coverage)</CardTitle>
                      <div className="flex flex-wrap items-center gap-2">
                        {owaspIds.length > 0 && (
                          <select
                            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                            value={filters.owaspId}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, owaspId: e.target.value, offset: 0 }))
                            }
                          >
                            <option value="">All coverage mappings</option>
                            {owaspIds.map((a) => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                        )}
                        {controlStatuses.length > 0 && (
                          <select
                            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                            value={filters.controlStatus}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, controlStatus: e.target.value, offset: 0 }))
                            }
                          >
                            <option value="">All statuses</option>
                            {controlStatuses.map((a) => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                        )}
                        {controlKeys.length > 0 && (
                          <select
                            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
                            value={filters.controlKey}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, controlKey: e.target.value, offset: 0 }))
                            }
                          >
                            <option value="">All controls</option>
                            {controlKeys.map((a) => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-auto pr-2">
                    {auditQuery.data?.domainLogs?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No records.</p>
                    ) : auditQuery.data?.domainLogs?.map((log: any, idx: number) => (
                      <div key={idx} className="p-2 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="font-mono break-all">{log.owasp_id || 'n/a'}</span>
                          <span>{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <div>Control: {log.control_key || 'n/a'}</div>
                        <div>Status: {log.status || 'n/a'}</div>
                        <div>Event: {log.event_type}</div>
                        <div>Severity: {log.severity}</div>
                        <div>Domain: {log.domain_id || 'n/a'}</div>
                        {log.summary && (
                          <div className="text-muted-foreground break-all">{log.summary}</div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <CurrencyDock />
    </div>
  );
}
